import { create } from "zustand";
import { currentLang, t, tx, type Lang } from "./i18n";
import type { Command, CommandResult, Company, GameState, LogEntry } from "@bd/sim";
import { registerRivals } from "@bd/sim";
import { SimHost } from "./sim/host";
import { sfx } from "./lib/sound";
import { wantsLaunchShow } from "./lib/launchPref";

export type Tool = null | "build" | "furnish" | "staff" | "projects" | "research" | "market" | "overlays";
export type Screen =
  | null
  | { kind: "design"; projectId?: string; licence?: string; back?: "playtest" }
  | { kind: "followup"; productId: string; line: "expansion" | "edition" | "spinoff" | "card_set" | "rpg_supplement" }
  | { kind: "meta"; productId: string }
  | { kind: "playtest"; projectId: string }
  | { kind: "print"; projectId?: string; productId?: string }
  | { kind: "reviews"; productId: string }
  | { kind: "market"; tab?: "trends" | "league" | "charts" | "fans" | "audiences" | "rivals" | "news" | "awards" | "notebook" }
  | { kind: "products" }
  | { kind: "staff" }
  | { kind: "finances" }
  | { kind: "research" }
  | { kind: "menu" }
  | { kind: "achievements" }
  | { kind: "decision"; id: string }
  | { kind: "finale" }
  | { kind: "launch"; productId: string }
  | { kind: "quarter" } // online: what happened while the quarter was settled
  | { kind: "feedback" }
  | { kind: "cousins" }
  | { kind: "imprints" }
  | { kind: "conventions" };

/** Online season the player is in (null in solo play). Filled by online/session.ts. */
export interface OnlineState {
  seasonId: string;
  name: string;
  seat: number;
  playerId: string;
  quarter: number;
  week: number;
  deadline: string | null;
  status: "lobby" | "playing" | "resolving" | "finished";
  ready: boolean;
  moves: Command[];
  seats: { seat: number; company: string; name: string; ready: boolean; here: boolean }[];
  settling: boolean;
  report: { quarter: number; lines: { text: string; tone: Toast["tone"] }[] } | null;
}

export interface Toast {
  id: number;
  title: string;
  text?: string;
  tone: "good" | "bad" | "info" | "warn";
  action?: { label: string; screen: Screen };
  at: number;
}

export interface UIState {
  game: GameState | null;
  online: OnlineState | null;
  /** title screen: showing the online lobby instead of the solo start */
  lobby: boolean;
  /** online: the cousin's company whose studio you're looking around (read-only) */
  visiting: string | null;
  /** when the latest state arrived and how long a week lasts, for smooth in-week animation */
  stateAt: number;
  msPerWeek: number;
  speed: number;
  /** The last running speed the player picked; resuming goes back to it. */
  lastSpeed: number;
  resume: () => void;
  tool: Tool;
  trayTab: "rooms" | "furniture" | "decor" | "plants";
  selectedRoom: string | null;
  /** a building next door you clicked (its annex kind), to show what buying it gives you */
  selectedLot: string | null;
  closeup: string | null; // room id being viewed close up
  screen: Screen;
  toasts: Toast[];
  placing: { kind: "room"; type: string } | { kind: "furniture"; type: string } | { kind: "door"; roomId: string } | null;
  onboarding: number; // 0..3 Gran's tips, 4 = done
  pausedFor: string | null;
  quality: "high" | "low";
  rot: number;
  buildGhost: { type: string; x: number; y: number; w: number; h: number; valid: boolean; price: number; why?: string; screen: { x: number; y: number } } | null;
  textScale: number;
  /** interface language (see i18n): kept here so a switch redraws everything */
  lang: Lang;
  error: string | null;
  set: (p: Partial<UIState>) => void;
  cmd: (c: Command) => Promise<CommandResult>;
  toast: (t: Omit<Toast, "id" | "at">) => void;
  dismiss: (id: number) => void;
  setSpeed: (s: number) => void;
}

export const host = new SimHost();
let toastId = 1;
/** News that arrived while a screen was open; the latest two show when it closes. */
let held: Omit<Toast, "id" | "at">[] = [];
/** a launch show that waited behind an open screen */
let pendingLaunch: string | null = null;
let lastLog = 0;

/** Online: every move that worked is recorded and sent to the server (see online/session.ts). */
let moveHook: ((c: Command) => void) | null = null;
export function onMove(f: ((c: Command) => void) | null): void {
  moveHook = f;
}
/** Which company is the player's: online it is their seat, not the first company. */
let myPlayer: string | null = null;
export function setMyPlayer(p: string | null): void {
  myPlayer = p;
}

export const useGame = create<UIState>((set, get) => ({
  game: null,
  online: null,
  visiting: null,
  lobby: typeof location !== "undefined" && /[?&](online|qa)\b/.test(location.search),
  stateAt: 0,
  msPerWeek: 0,
  speed: 0,
  lastSpeed: 1,
  tool: null,
  trayTab: "rooms",
  selectedRoom: null,
  selectedLot: null,
  closeup: null,
  screen: null,
  toasts: [],
  placing: null,
  onboarding: 0,
  pausedFor: null,
  quality: initialQuality(),
  rot: 0,
  buildGhost: null,
  textScale: Number(localStorage.getItem("bd:textScale") ?? 1),
  lang: currentLang(),
  error: null,
  set: (p) => set(p),
  cmd: async (c) => {
    if (get().visiting) return { ok: false, error: t("You're visiting: go back to your studio first.") };
    const r = await host.command(c);
    if (r.ok) moveHook?.(c);
    return r;
  },
  toast: (t) => {
    sfx(t.tone === "bad" || t.tone === "warn" ? "thud" : "toast");
    const id = toastId++;
    // three at most: the oldest makes way
    set((s) => ({ toasts: [...s.toasts.slice(-2), { ...t, id, at: Date.now() }] }));
    setTimeout(() => get().dismiss(id), 7000);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setSpeed: (speed) => {
    // online, time moves a quarter at a time when everyone is ready
    if (get().online) return;
    set((s) => ({ speed, pausedFor: null, lastSpeed: speed > 0 ? speed : s.lastSpeed }));
    host.setSpeed(speed);
  },
  resume: () => {
    if (get().speed === 0) get().setSpeed(get().lastSpeed);
  },
}));

export function me(g: GameState | null): Company | null {
  if (!g) return null;
  return (myPlayer && g.companies.find((c) => c.player === myPlayer)) || g.companies[0] || null;
}

const TOAST_KINDS: Record<string, Toast["tone"]> = {
  release: "good", phase: "info", soldout: "good", misprint: "bad", returns: "warn", revival: "good", hire: "info", quit: "bad",
  rescue: "warn", overdraft: "bad", bankrupt: "bad", combo: "good", playtest: "info", fix: "good", research: "info", levelup: "good",
  // print is left out: the print screen already says "1,000 copies ordered"
  trait: "info", move: "good", build: "info", poach: "warn", training: "good", shelf: "warn", goal: "good", direction: "good", finale: "good",
  achievement: "good", arc: "info", unlock: "good", trend: "info", licence: "info", pitch: "info", shelf_fee: "warn", rival_move: "info", checkin: "info", trophy: "good", retire: "info", fanmail: "good", fanmail_late: "warn", booth: "good", decade: "good", imprint: "info", sketch: "info", news_event: "info", crowd: "good", preorder: "info", meta: "warn", ban: "info", rotate: "info", community: "good",
};

function toastFor(e: LogEntry, g: GameState): Omit<Toast, "id" | "at"> | null {
  const tone = TOAST_KINDS[e.kind];
  if (!tone) return null;
  // the playtest screen shows the round as it plays out; a toast with the result would spoil it
  if ((e.kind === "playtest" || e.kind === "fix") && useGame.getState().screen?.kind === "playtest") return null;
  const co = me(g);
  let action: Toast["action"];
  if (e.kind === "release" && co) {
    // longest name first: "Game 16" also contains "Game 1"
    const p = co.products.filter((x) => x.launched && e.text.includes(x.design.name)).sort((a, b) => b.design.name.length - a.design.name.length)[0];
    if (p) action = { label: t("Read reviews"), screen: { kind: "reviews", productId: p.id } };
  }
  if (e.kind === "phase" && co) {
    const p = co.projects.filter((x) => e.text.startsWith(x.design.name)).sort((a, b) => b.design.name.length - a.design.name.length)[0];
    if (p) action = p.phase === "ready" ? { label: t("Print it"), screen: { kind: "print", projectId: p.id } } : { label: t("Playtest"), screen: { kind: "playtest", projectId: p.id } };
  }
  if (e.kind === "pitch") {
    const [who, rest] = e.text.split(" pitches ");
    return { title: t("A pitch from {who}", { who: who.replace(/ \(.*\)$/, "") }), text: rest ? txLine(rest.charAt(0).toUpperCase() + rest.slice(1)) : undefined, tone, action: { label: t("See the pitch"), screen: { kind: "products" } } };
  }
  if (e.kind === "release" && co && action) {
    const p = co.products.find((x) => x.id === (action!.screen as { productId: string }).productId)!;
    return { title: t("{name} is out", { name: p.design.name }), text: t("Reviews average {score}.", { score: p.score.toFixed(1) }), tone, action };
  }
  if (e.kind === "achievement") {
    const [name, ...desc] = e.text.split(": ");
    return { title: t("Achievement: {name}", { name: t(name) }), text: t(desc.join(": ")), tone };
  }
  let text = co ? youVoice(e.text, co.name) : e.text;
  // Ukrainian: the toast's own wording ("Нова кімната: ..."), else the log line as written
  if (currentLang() !== "en") {
    const own = txLine(text);
    text = own !== text ? own : txLine(e.text);
  }
  // a game called "Crypt Dash: the North" must not be cut at its own colon
  const game = co ? [...co.projects.map((p) => p.design.name), ...co.products.map((p) => p.design.name)].filter((n) => n.includes(":") && text.startsWith(n)).sort((a, b) => b.length - a.length)[0] : undefined;
  const [title, ...rest] = game ? [game, text.slice(game.length).replace(/^:\s*/, "")] : text.split(": ");
  let body = rest.length ? rest.join(": ") : undefined;
  // the button says what to do next, so the toast only says what happened
  if (body && action) body = body.split(/(?<=\.)\s/)[0];
  return { title: rest.length ? title : text, text: body ? body.charAt(0).toUpperCase() + body.slice(1) : undefined, tone, action };
}

const PAST: Record<string, string> = { sets: "set", puts: "put", cuts: "cut", lets: "let", hits: "hit", quits: "quit", bets: "bet", splits: "split", builds: "built", releases: "released", researches: "researched", prints: "printed", starts: "started", moves: "moved", opens: "opened", hires: "hired", goes: "went", was: "were", is: "are", buys: "bought", wins: "won", becomes: "became", has: "have", gets: "got", takes: "took", makes: "made", leaves: "left", pays: "paid", sells: "sold", signs: "signed", runs: "ran", gives: "gave", outbids: "outbid", sends: "sent", holds: "held", keeps: "kept" };

/** Log lines name the company ("Box & Dice Co. builds a design room"); toasts speak to the player ("You built a design room"). */
function youVoice(text: string, company: string): string {
  // the company, or the founder (who is called "You" on the roster)
  const who = text.startsWith(`${company} `) ? company : text.startsWith("You ") ? "You" : null;
  if (!who) return text;
  const rest = text.slice(who.length + 1);
  const [verb, ...more] = rest.split(" ");
  return `You ${past(verb)} ${more.join(" ")}`;
}

/**
 * A line the rules engine wrote (a log line, news, a report line) in the player's language. tx() finds the whole
 * line's template; a line glued from parts ("Power cut: Work by candlelight.", "New goals for 1990: a; b.") is
 * translated part by part, and only when every part is known. English comes back as it is.
 */
export function txLine(text: string): string {
  if (currentLang() === "en" || !text) return text;
  return txParts(text, 6) ?? text;
}

function txParts(s: string, depth: number): string | null {
  const whole = tx(s);
  if (whole !== s && !leftoverEnglish(whole)) return whole;
  // a goal or a label the engine wrote in lower case inside a longer line
  if (/^[a-z]/.test(s)) {
    const cap = s.charAt(0).toUpperCase() + s.slice(1);
    const up = tx(cap);
    if (up !== cap && !leftoverEnglish(up)) return up.charAt(0).toLowerCase() + up.slice(1);
  }
  if (depth <= 0) return null;
  for (const sep of [": ", "; ", ". "]) {
    // a sentence break keeps its full stop on the left
    const keep = sep === ". " ? 1 : 0;
    for (let i = s.indexOf(sep); i > 0; i = s.indexOf(sep, i + 1)) {
      const a = txParts(s.slice(0, i + keep), depth - 1);
      if (a === null) continue;
      const b = txParts(s.slice(i + sep.length), depth - 1);
      if (b !== null) return a + sep.slice(keep) + b;
    }
  }
  // "a; b." : the list without its full stop
  if (s.endsWith(".") && !s.endsWith("..")) {
    const r = txParts(s.slice(0, -1), depth - 1);
    if (r !== null) return r.endsWith(".") ? r : `${r}.`;
  }
  return null;
}

/**
 * A short template ("{rival} copies {game}") can match a longer English line and pass most of it through
 * ("sell 5,000 копіює this year"). A result is not a translation if, once names are taken out (the players'
 * companies and games, and anything in Title Case like "Rally of the High Road"), a lower-case English word is left.
 */
function leftoverEnglish(s: string): boolean {
  if (!/[a-z]{2}/.test(s)) return false;
  let rest = s;
  for (const n of playerNames()) if (rest.includes(n)) rest = rest.split(n).join(" ");
  rest = rest.replace(/\{\w+\}/g, " ").replace(/\b[a-z]+'s\b/g, " ").replace(/(?:[A-Z0-9][\w'’-]*|&)(?:\s+(?:[a-z]{1,4}\s+)*(?:[A-Z0-9][\w'’-]*|&))*/g, " ");
  return /\b[a-z]{2,}\b/.test(rest);
}

/** Names the players typed (companies, games), which may be lower case: they stay as they are in any language. */
let namesFor: GameState | null = null;
let names: string[] = [];
function playerNames(): string[] {
  const g = useGame.getState().game;
  if (g !== namesFor) {
    namesFor = g;
    names = g ? [...new Set(g.companies.flatMap((c) => [c.name, ...c.products.map((p) => p.design.name), ...c.projects.map((p) => p.design.name)]))].filter((n) => /(?:^|\s)[a-z]/.test(n)).sort((a, b) => b.length - a.length) : [];
  }
  return names;
}

/** "reaches" → "reached", "finishes" → "finished", "hires" → "hired", with the irregular ones listed. */
function past(verb: string): string {
  if (PAST[verb]) return PAST[verb];
  if (/(ch|sh|ss|x)es$/.test(verb)) return verb.slice(0, -2) + "ed";
  if (/ies$/.test(verb)) return verb.slice(0, -3) + "ied";
  if (/es$/.test(verb) && /[^aeiou]e$/.test(verb.slice(0, -1))) return verb.slice(0, -1) + "d";
  if (/[^s]s$/.test(verb)) return verb.slice(0, -1) + "ed";
  return verb;
}

host.on((m) => {
  const s = useGame.getState();
  if (m.type === "state") {
    const g = m.state;
    // publishers invented during the season live in the save: teach this thread their names
    registerRivals(g);
    const co = me(g);
    // reaching the 2035 finale unlocks New Game+, which keeps what your notebook learned
    if (g.finished && co) {
      try {
        if (!localStorage.getItem("bd:ngplus")) localStorage.setItem("bd:ngplus", JSON.stringify({ notebook: co.notebook, from: co.name }));
      } catch {
        // storage blocked: New Game+ just stays locked on this device
      }
    }
    // new log lines become toasts (only ours)
    // a different game (new or loaded): start the log here, don't replay history as toasts
    // lastLog counts every line ever written; old lines are dropped from the front once a year (logBase)
    const total = (g.logBase ?? 0) + g.log.length;
    const switched = !s.game || s.game.seed !== g.seed || lastLog > total || Math.abs(s.game.week - g.week) > 3;
    if (switched) lastLog = total;
    const fresh = g.log.slice(Math.max(0, lastLog - (g.logBase ?? 0)));
    lastLog = total;
    useGame.setState({ game: g, stateAt: performance.now(), msPerWeek: m.msPerWeek });
    if (s.game) for (const e of fresh) if (e.company === co?.id) {
      // launch day gets its own show (the first ten games, then big boxes, epics and hits)
      if (e.kind === "release" && co) {
        const p = co.products.filter((x) => x.launched && e.text.includes(x.design.name)).sort((a, b) => b.design.name.length - a.design.name.length)[0];
        if (p && wantsLaunchShow(co.stats.released, p.design.scope)) {
          // a screen is open: the show waits until you close it
          const open = useGame.getState().screen;
          if (!open || open.kind === "products") useGame.setState({ screen: { kind: "launch", productId: p.id } });
          else pendingLaunch = p.id;
          continue;
        }
      }
      if (e.kind === "levelup") sfx("levelup");
      if (e.kind === "soldout" || e.kind === "goal") sfx("cash");
      const t = toastFor(e, g);
      if (!t) continue;
      // news waits while a screen is open, so it never covers what you're working on
      if (useGame.getState().screen) held = [...held.slice(-1), t];
      else s.toast(t);
    }
    // the autosave (every 13 weeks) runs in the sim worker, off this thread
  } else if (m.type === "pause") {
    useGame.setState({ speed: 0, pausedFor: m.reason });
  } else if (m.type === "error") {
    console.error(m.message);
    useGame.setState({ error: m.message });
  }
});

/** The saved choice, else High unless WebGL runs in software (no GPU), where the post effects cost seconds a frame. */
function initialQuality(): "high" | "low" {
  try {
    const saved = localStorage.getItem("bd:quality");
    if (saved === "high" || saved === "low") return saved;
  } catch {
    // storage blocked: fall through to detection
  }
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    const info = gl?.getExtension("WEBGL_debug_renderer_info");
    const renderer = info ? String(gl!.getParameter(info.UNMASKED_RENDERER_WEBGL)) : "";
    if (/swiftshader|llvmpipe|software|basic render/i.test(renderer)) return "low";
  } catch {
    // no WebGL details: assume a GPU
  }
  return "high";
}

// a screen just closed: show the news that waited behind it
useGame.subscribe((st, prev) => {
  // a screen just opened: the toasts behind it are about what you're now doing, so they go
  if (!prev.screen && st.screen && st.toasts.length) useGame.setState({ toasts: [] });
  if (prev.screen && !st.screen && pendingLaunch && prev.screen.kind !== "launch") {
    const id = pendingLaunch;
    pendingLaunch = null;
    useGame.setState({ screen: { kind: "launch", productId: id } });
    return;
  }
  if (prev.screen && !st.screen && held.length) {
    const out = held;
    held = [];
    for (const t of out) st.toast(t);
  }
});
